import React from 'react'
import { Link, Navigate, useSearchParams } from 'react-router'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { hexColors } from '@mobi/settings/hexColors'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { spacing } from '@mobi/component-library/Theme/Common'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { AppRoutes } from '@core/AppRoutes'
import { MainContainer } from '@core/Components/Containers'
import { useFeature } from '@core/Utils/hooks/useFeature'
import type { FollowMeTabNav } from '@core/Areas/FollowMe/types'
import { FollowingFeed } from '@core/Areas/FollowMe/Components/FollowingFeed'
import { ExploreFeed } from '@core/Areas/FollowMe/Components/ExploreFeed'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import { trackFollowMeViewMyProfile } from '@core/Areas/FollowMe/helpers/analytics'
import { useValidatedTabParam } from './hooks/useValidatedTabParam'
import { followMePrincipleProfileQuery } from '../../helpers/queries'

export const FeedPage = () => {
  const isFollowMeFeatureActive = useFeature('content-hub-follow-me')
  const { data: myProfile } = useQuery(followMePrincipleProfileQuery)

  const [searchParams, setSearchParams] = useSearchParams(createTabNavSearchParam('explore'))
  const currentTab = getCurrentTabFromSearchParam(searchParams)

  useValidatedTabParam()

  if (!isFollowMeFeatureActive) {
    return <Navigate to={AppRoutes.Home} />
  }

  const handleTabClick = (tab: FollowMeTabNav) => () => {
    setSearchParams(createTabNavSearchParam(tab))
  }

  return (
    <MainContainer background='surface300'>
      <WrapperStyled>
        <header>
          <span>
            <SvgLazy width='11rem' color='' name='ExpertHubLogo' />
          </span>

          <Link to={AppRoutes.FollowMeMyProfile} onClick={trackFollowMeViewMyProfile}>
            <ProfileAvatar avatar={myProfile?.avatar} color={myProfile?.color} />
          </Link>
        </header>

        <Tabs>
          <TabButton
            content='Explore'
            isSelected={currentTab === 'explore'}
            onClick={handleTabClick('explore')}
          />

          <TabButton
            content='Following'
            isSelected={currentTab === 'following'}
            onClick={handleTabClick('following')}
          />
        </Tabs>

        <div>
          {currentTab === 'explore' ? <ExploreFeed /> : null}

          {currentTab === 'following' ? <FollowingFeed /> : null}
        </div>
      </WrapperStyled>
    </MainContainer>
  )
}

// =============
// Local Helpers
// =============

const createTabNavSearchParam = (tab: FollowMeTabNav) => ({ tab })

const getCurrentTabFromSearchParam = (searchParams: URLSearchParams): FollowMeTabNav => {
  const allowedTabs: FollowMeTabNav[] = ['explore', 'following']
  const currentTab: FollowMeTabNav = (searchParams.get('tab') as FollowMeTabNav) || 'explore'

  if (allowedTabs.includes(currentTab)) return currentTab
  return 'explore'
}

// =======
// Styles
// =======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',

  '> header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '5.2rem',
    overflow: 'hidden',
    padding: `0 ${spacing.md}`,
    background: hexColors.blueDiamond,

    '> a': {
      textDecoration: 'none',
      color: 'unset',
      userSelect: 'none',
      transition: 'opacity 0.25s ease-out',

      '@media (hover: hover) and (pointer: fine)': {
        ':hover': { opacity: 0.75 },
      },

      ':active': { opacity: 0.5 },
    },
  },
})
